import DOMPurify, { type Config } from "dompurify";

/**
 * Letters in the browser. The text a user writes becomes simple HTML
 * (paragraphs and line breaks, everything escaped), and a template's HTML
 * becomes text to edit. A letter is shown only after it is cleaned here
 * (crm-api has cleaned it already: this is the second layer) and only inside
 * a sandboxed iframe without scripts, whose CSP loads nothing from outside.
 */

const escapeHtml = (s: string) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/** text as typed → <p>…</p> per paragraph, <br> per line */
export const textToHtml = (text: string): string =>
  text
    .replace(/\r\n?/g, "\n")
    .trim()
    .split(/\n{2,}/)
    .filter((p) => p.trim())
    .map((p) => `<p>${escapeHtml(p).replace(/\n/g, "<br>")}</p>`)
    .join("");

/** HTML → text to edit: a line per <br>, an empty line between paragraphs */
export const htmlToText = (html: string): string => {
  const doc = new DOMParser().parseFromString(
    html
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/(p|div|h[1-6]|li|blockquote|tr)>/gi, "$&\n\n"),
    "text/html",
  );
  return (doc.body.textContent ?? "")
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const CONFIG: Config = {
  FORBID_TAGS: [
    "script",
    "style",
    "form",
    "input",
    "button",
    "textarea",
    "select",
    "iframe",
    "frame",
    "object",
    "embed",
    "base",
    "meta",
    "link",
    "svg",
    "math",
    "template",
  ],
  FORBID_ATTR: [
    "srcset",
    "background",
    "poster",
    "action",
    "formaction",
    "ping",
  ],
  ALLOW_DATA_ATTR: false,
  ADD_ATTR: ["data-vv-src", "target"],
};

const LOADS = /url\s*\(|@import|expression\s*\(|image-set\s*\(/i;

/**
 * Cleans a letter for showing. Remote pictures stay blocked unless
 * showImages: then an http(s) address crm-api kept aside (data-vv-src)
 * becomes the src again.
 */
export const cleanMailHtml = (html: string, showImages = false): string => {
  const purify = DOMPurify();
  purify.addHook("uponSanitizeAttribute", (_node, data) => {
    if (data.attrName === "style" && LOADS.test(data.attrValue)) {
      data.keepAttr = false;
    }
    if (
      data.attrName === "data-vv-src" &&
      !/^https?:\/\//i.test(data.attrValue)
    ) {
      data.keepAttr = false;
    }
  });
  purify.addHook("afterSanitizeAttributes", (node) => {
    const el = node as Element;
    if (el.tagName === "A") {
      if (el.hasAttribute("href")) {
        el.setAttribute("target", "_blank");
        el.setAttribute("rel", "noopener noreferrer");
      } else {
        el.removeAttribute("target");
      }
    }
    if (el.tagName === "IMG") {
      const src = el.getAttribute("src");
      if (src && !/^\s*(data:image\/|cid:)/i.test(src)) {
        el.removeAttribute("src");
        if (/^https?:\/\//i.test(src.trim())) {
          el.setAttribute("data-vv-src", src.trim());
        }
      }
      const kept = el.getAttribute("data-vv-src");
      if (showImages && kept && /^https?:\/\//i.test(kept)) {
        el.setAttribute("src", kept);
      }
    }
  });
  return String(purify.sanitize(html, CONFIG));
};

/** whether the letter has remote pictures that are blocked now */
export const hasBlockedImages = (html: string) => /data-vv-src=/i.test(html);

/**
 * The document of the sandboxed iframe: the cleaned letter, a CSP that runs
 * no script and loads nothing from outside (only pictures from the letter
 * itself, or remote ones when the reader asked), and base styles.
 */
export const mailDocument = (
  html: string,
  { showImages = false }: { showImages?: boolean } = {},
): string => {
  const img = showImages ? "data: cid: https: http:" : "data: cid:";
  const csp = `default-src 'none'; img-src ${img}; style-src 'unsafe-inline'; base-uri 'none'; form-action 'none'`;
  // letters are made for a white page, so they are shown on one in both themes
  return (
    `<!doctype html><html><head><meta charset="utf-8">` +
    `<meta http-equiv="Content-Security-Policy" content="${csp}">` +
    `<style>:root{color-scheme:light}html,body{background:#fff}` +
    `body{margin:0;padding:8px;font:14px/1.5 system-ui,sans-serif;color:#171717;` +
    `word-break:break-word;overflow-wrap:anywhere}img{max-width:100%;height:auto}` +
    `table{max-width:100%}a{color:#1d4ed8}</style></head>` +
    `<body>${cleanMailHtml(html, showImages)}</body></html>`
  );
};
